import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiImage, FiVideo, FiX } from 'react-icons/fi';
import AdminShell from '../../components/AdminShell/AdminShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import AdminWalletBadge from '../../components/AdminWalletBadge/AdminWalletBadge';
import panelStyles from '../../styles/adminPanels.module.css';
import styles from './AdminContent.module.css';
import axiosClient from '../../api/axiosClient';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');
const DEFAULT_CATEGORIES = ['Blockchain', 'Crypto', 'DeFi', 'AI', 'Security', 'Guides', 'Fintech', 'Marketing', 'CRM'];

const BLANK_POST = { title: '', category: 'Blockchain', description: '', content: '', featured: false, published: true, displayOrder: 0 };
const BLANK_VIDEO = { title: '', videoUrl: '', disclaimer: '', displayOrder: 0, published: true };
const BLANK_LOGO = { name: '', linkUrl: '', displayOrder: 0, published: true };

const AdminContent: React.FC = () => {
  const [tab, setTab] = useState<'blog' | 'video' | 'partner'>('blog');
  const [posts, setPosts] = useState<any[]>([]);
  const [videos, setVideos] = useState<any[]>([]);
  const [partnerLogos, setPartnerLogos] = useState<any[]>([]);
  const [categories, setCategories] = useState<string[]>(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(true);

  const [editingPost, setEditingPost] = useState<any>(null);
  const [postForm, setPostForm] = useState<any>(BLANK_POST);
  const [postImage, setPostImage] = useState<File | null>(null);

  const [editingVideo, setEditingVideo] = useState<any>(null);
  const [videoForm, setVideoForm] = useState<any>(BLANK_VIDEO);
  const [videoThumb, setVideoThumb] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);

  const [editingLogo, setEditingLogo] = useState<any>(null);
  const [logoForm, setLogoForm] = useState<any>(BLANK_LOGO);
  const [logoFile, setLogoFile] = useState<File | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      axiosClient.get('/admin/blog-posts'),
      axiosClient.get('/admin/story-videos'),
      axiosClient.get('/admin/partner-logos'),
      axiosClient.get('/admin/blog-categories'),
    ]).then(([p, v, l, cat]) => {
      setPosts(p.data.posts); setVideos(v.data.videos); setPartnerLogos(l.data.logos);
      const merged = Array.from(new Set([...DEFAULT_CATEGORIES, ...(cat.data.categories || [])])).sort();
      setCategories(merged);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // ---------- Blog posts ----------
  const openPostForm = (post?: any) => {
    if (post) {
      setEditingPost(post);
      setPostForm({
        title: post.title, category: post.category, description: post.description || '',
        content: post.content || '', featured: post.featured, published: post.published, displayOrder: post.display_order,
      });
    } else {
      setEditingPost('new');
      setPostForm(BLANK_POST);
    }
    setPostImage(null);
  };

  const savePost = async () => {
    if (!postForm.title.trim()) { toast.error('Title is required.'); return; }
    const fd = new FormData();
    Object.entries(postForm).forEach(([k, v]) => fd.append(k, String(v)));
    if (postImage) fd.append('image', postImage);
    try {
      if (editingPost === 'new') {
        await axiosClient.post('/admin/blog-posts', fd);
        toast.success('Blog post created.');
      } else {
        await axiosClient.patch(`/admin/blog-posts/${editingPost.id}`, fd);
        toast.success('Blog post updated.');
      }
      setEditingPost(null);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not save post.');
    }
  };

  const deletePost = async (id: string) => {
    if (!window.confirm('Delete this blog post?')) return;
    await axiosClient.delete(`/admin/blog-posts/${id}`);
    toast.success('Deleted.');
    load();
  };

  // ---------- Story videos ----------
  const openVideoForm = (video?: any) => {
    if (video) {
      setEditingVideo(video);
      setVideoForm({
        title: video.title, videoUrl: video.video_url || '', disclaimer: video.disclaimer || '',
        displayOrder: video.display_order, published: video.published,
      });
    } else {
      setEditingVideo('new');
      setVideoForm(BLANK_VIDEO);
    }
    setVideoThumb(null);
    setVideoFile(null);
  };

  const saveVideo = async () => {
    if (!videoForm.title.trim()) { toast.error('Title is required.'); return; }
    const fd = new FormData();
    Object.entries(videoForm).forEach(([k, v]) => fd.append(k, String(v)));
    if (videoThumb) fd.append('thumbnail', videoThumb);
    if (videoFile) fd.append('video', videoFile);
    try {
      if (editingVideo === 'new') {
        await axiosClient.post('/admin/story-videos', fd);
        toast.success('Video added.');
      } else {
        await axiosClient.patch(`/admin/story-videos/${editingVideo.id}`, fd);
        toast.success('Video updated.');
      }
      setEditingVideo(null);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not save video.');
    }
  };

  const deleteVideo = async (id: string) => {
    if (!window.confirm('Delete this video?')) return;
    await axiosClient.delete(`/admin/story-videos/${id}`);
    toast.success('Deleted.');
    load();
  };

  // ---------- Partner network logos ----------
  const openLogoForm = (logo?: any) => {
    if (logo) {
      setEditingLogo(logo);
      setLogoForm({ name: logo.name, linkUrl: logo.link_url || '', displayOrder: logo.display_order, published: logo.published });
    } else {
      setEditingLogo('new');
      setLogoForm(BLANK_LOGO);
    }
    setLogoFile(null);
  };

  const saveLogo = async () => {
    if (!logoForm.name.trim()) { toast.error('Name is required.'); return; }
    const fd = new FormData();
    Object.entries(logoForm).forEach(([k, v]) => fd.append(k, String(v)));
    if (logoFile) fd.append('logo', logoFile);
    try {
      if (editingLogo === 'new') {
        await axiosClient.post('/admin/partner-logos', fd);
        toast.success('Partner added.');
      } else {
        await axiosClient.patch(`/admin/partner-logos/${editingLogo.id}`, fd);
        toast.success('Partner updated.');
      }
      setEditingLogo(null);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not save.');
    }
  };

  const deleteLogo = async (id: string) => {
    if (!window.confirm('Delete this partner logo?')) return;
    await axiosClient.delete(`/admin/partner-logos/${id}`);
    toast.success('Deleted.');
    load();
  };

  return (
    <AdminShell>
      <PageHeader
        title="Landing Page Content"
        subtitle="Manage the Blog / Insights section and the Our Story in Motion video carousel."
        action={<AdminWalletBadge />}
      />
      <div className={panelStyles.page}>
        <div className={panelStyles.filters} style={{ marginBottom: 20 }}>
          <button className={`${panelStyles.filterBtn} ${tab === 'blog' ? panelStyles.filterBtnActive : ''}`} onClick={() => setTab('blog')}>Blog Posts</button>
          <button className={`${panelStyles.filterBtn} ${tab === 'video' ? panelStyles.filterBtnActive : ''}`} onClick={() => setTab('video')}>Story Videos</button>
          <button className={`${panelStyles.filterBtn} ${tab === 'partner' ? panelStyles.filterBtnActive : ''}`} onClick={() => setTab('partner')}>Partner Network</button>
        </div>

        {loading && <div className={panelStyles.loading}><span className={panelStyles.spinnerLg} /></div>}

        {!loading && tab === 'blog' && (
          <>
            <button className={styles.addBtn} onClick={() => openPostForm()}><FiPlus /> New Blog Post</button>
            {posts.length === 0 && <div className={panelStyles.empty}>No blog posts yet.</div>}
            <div className={styles.grid}>
              {posts.map((post) => (
                <div key={post.id} className={styles.card}>
                  <div className={styles.cardImgWrap}>
                    {post.image_path ? <img src={`${API_ORIGIN}${post.image_path}`} alt="" /> : <FiImage className={styles.cardImgPlaceholder} />}
                    {!post.published && <span className={styles.draftTag}>Draft</span>}
                  </div>
                  <div className={styles.cardBody}>
                    <span className={styles.cardCategory}>{post.category}</span>
                    <b>{post.title}</b>
                    <div className={styles.cardActions}>
                      <button onClick={() => openPostForm(post)}><FiEdit2 /> Edit</button>
                      <button onClick={() => deletePost(post.id)}><FiTrash2 /> Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {!loading && tab === 'video' && (
          <>
            <button className={styles.addBtn} onClick={() => openVideoForm()}><FiPlus /> New Video</button>
            {videos.length === 0 && <div className={panelStyles.empty}>No videos yet.</div>}
            <div className={styles.grid}>
              {videos.map((video) => (
                <div key={video.id} className={styles.card}>
                  <div className={styles.cardImgWrap}>
                    {video.thumbnail_path ? <img src={`${API_ORIGIN}${video.thumbnail_path}`} alt="" /> : <FiVideo className={styles.cardImgPlaceholder} />}
                    {!video.published && <span className={styles.draftTag}>Draft</span>}
                  </div>
                  <div className={styles.cardBody}>
                    <b>{video.title}</b>
                    <div className={styles.cardActions}>
                      <button onClick={() => openVideoForm(video)}><FiEdit2 /> Edit</button>
                      <button onClick={() => deleteVideo(video.id)}><FiTrash2 /> Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {!loading && tab === 'partner' && (
          <>
            <button className={styles.addBtn} onClick={() => openLogoForm()}><FiPlus /> New Partner</button>
            {partnerLogos.length === 0 && <div className={panelStyles.empty}>No partners yet.</div>}
            <div className={styles.grid}>
              {partnerLogos.map((logo) => (
                <div key={logo.id} className={styles.card}>
                  <div className={styles.cardImgWrap}>
                    {logo.logo_path ? <img src={`${API_ORIGIN}${logo.logo_path}`} alt="" /> : <FiImage className={styles.cardImgPlaceholder} />}
                    {!logo.published && <span className={styles.draftTag}>Draft</span>}
                  </div>
                  <div className={styles.cardBody}>
                    <b>{logo.name}</b>
                    <div className={styles.cardActions}>
                      <button onClick={() => openLogoForm(logo)}><FiEdit2 /> Edit</button>
                      <button onClick={() => deleteLogo(logo.id)}><FiTrash2 /> Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {editingPost && (
        <div className={styles.modalOverlay} onClick={() => setEditingPost(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{editingPost === 'new' ? 'New Blog Post' : 'Edit Blog Post'}</h3>
              <button onClick={() => setEditingPost(null)}><FiX /></button>
            </div>
            <label>Title</label>
            <input value={postForm.title} onChange={(e) => setPostForm({ ...postForm, title: e.target.value })} />
            <label>Category</label>
            <input
              value={postForm.category}
              onChange={(e) => setPostForm({ ...postForm, category: e.target.value })}
              list="blog-category-options"
              placeholder="Choose or type a new category…"
            />
            <datalist id="blog-category-options">
              {categories.map((c) => <option key={c} value={c} />)}
            </datalist>
            <label>Short Description</label>
            <textarea rows={2} value={postForm.description} onChange={(e) => setPostForm({ ...postForm, description: e.target.value })} />
            <label>Full Content (optional)</label>
            <textarea rows={4} value={postForm.content} onChange={(e) => setPostForm({ ...postForm, content: e.target.value })} />
            <label>Cover Image</label>
            <input type="file" accept="image/*" onChange={(e) => setPostImage(e.target.files?.[0] || null)} />
            <div className={styles.modalRow}>
              <label className={styles.checkboxLabel}>
                <input type="checkbox" checked={postForm.featured} onChange={(e) => setPostForm({ ...postForm, featured: e.target.checked })} /> Featured
              </label>
              <label className={styles.checkboxLabel}>
                <input type="checkbox" checked={postForm.published} onChange={(e) => setPostForm({ ...postForm, published: e.target.checked })} /> Published
              </label>
            </div>
            <button className={styles.saveBtn} onClick={savePost}>Save Post</button>
          </div>
        </div>
      )}

      {editingVideo && (
        <div className={styles.modalOverlay} onClick={() => setEditingVideo(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{editingVideo === 'new' ? 'New Video' : 'Edit Video'}</h3>
              <button onClick={() => setEditingVideo(null)}><FiX /></button>
            </div>
            <label>Title</label>
            <input value={videoForm.title} onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })} />
            <label>Video Link (YouTube, etc — optional)</label>
            <input value={videoForm.videoUrl} onChange={(e) => setVideoForm({ ...videoForm, videoUrl: e.target.value })} placeholder="https://…" />
            <label>— OR — Upload a Video File (optional, max 150MB)</label>
            <input type="file" accept="video/*" onChange={(e) => setVideoFile(e.target.files?.[0] || null)} />
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: -6 }}>
              Give either a link or upload a file — if both are set, the uploaded file plays directly on the page and the link is ignored.
            </div>
            <label>Disclaimer Text (optional)</label>
            <textarea rows={3} value={videoForm.disclaimer} onChange={(e) => setVideoForm({ ...videoForm, disclaimer: e.target.value })} />
            <label>Thumbnail Image</label>
            <input type="file" accept="image/*" onChange={(e) => setVideoThumb(e.target.files?.[0] || null)} />
            <div className={styles.modalRow}>
              <label className={styles.checkboxLabel}>
                <input type="checkbox" checked={videoForm.published} onChange={(e) => setVideoForm({ ...videoForm, published: e.target.checked })} /> Published
              </label>
            </div>
            <button className={styles.saveBtn} onClick={saveVideo}>Save Video</button>
          </div>
        </div>
      )}

      {editingLogo && (
        <div className={styles.modalOverlay} onClick={() => setEditingLogo(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{editingLogo === 'new' ? 'New Partner' : 'Edit Partner'}</h3>
              <button onClick={() => setEditingLogo(null)}><FiX /></button>
            </div>
            <label>Partner Name</label>
            <input value={logoForm.name} onChange={(e) => setLogoForm({ ...logoForm, name: e.target.value })} />
            <label>Link URL (optional)</label>
            <input value={logoForm.linkUrl} onChange={(e) => setLogoForm({ ...logoForm, linkUrl: e.target.value })} placeholder="https://…" />
            <label>Logo Image</label>
            <input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
            <div className={styles.modalRow}>
              <label className={styles.checkboxLabel}>
                <input type="checkbox" checked={logoForm.published} onChange={(e) => setLogoForm({ ...logoForm, published: e.target.checked })} /> Published
              </label>
            </div>
            <button className={styles.saveBtn} onClick={saveLogo}>Save Partner</button>
          </div>
        </div>
      )}
    </AdminShell>
  );
};

export default AdminContent;
